<script setup lang="ts">
/**
 * App shell — bootstraps auth on mount and gates rendering behind the
 * auth-state resolution. While loading, shows a branded splash; once resolved,
 * delegates routing to the Vue Router (and its navigation guard).
 *
 * bead: lucky-war-hw7.1
 */
import { computed, onMounted } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useAuth, AuthState } from '@/composables/useAuth';
import { initLiff, liffSessionExchange } from '@/composables/useLiff';
import AppHeader from '@/components/AppHeader.vue';

const { state, bootstrapAuth, setPending } = useAuth();
const route = useRoute();
const publicRoutes = new Set(['login', 'pending', 'revoked']);
const showAppHeader = computed(() => (
  state.value === AuthState.Approved && !publicRoutes.has(route.name as string)
));

function focusMainContent() {
  document.getElementById('main-content')?.focus();
}

onMounted(async () => {
  // LIFF (LINE in-app browser): exchange the LIFF-issued token for a PB
  // session BEFORE the /status bootstrap so login is seamless (bead
  // lucky-war-hw7.5, design: docs/plans/LIFF-INTEGRATION.md §1-2). Zero-cost
  // no-op in normal browsers — the SDK is only fetched when VITE_LIFF_ID is
  // set AND the UA is LINE's in-app browser.
  const inLiff = await initLiff();
  if (inLiff) {
    const res = await liffSessionExchange();
    if (res === 'pending') {
      // Same pending-activation state the OAuth callback redirects to.
      // Set BEFORE bootstrapAuth so it early-returns and the guard shows
      // PendingView (403 body carries the display_name; the cookie-less
      // /status call cannot).
      setPending();
    }
  }
  bootstrapAuth();
});
</script>

<template>
  <div class="app">
    <!-- Branded splash while /status is in flight -->
    <div v-if="state === AuthState.Loading" class="app-splash">
      <div class="app-splash__inner">
        <span class="app-splash__wordmark">
          LUCKY<span class="app-splash__wordmark-accent">WAR</span>
        </span>
        <span class="app-splash__spinner"></span>
      </div>
    </div>

    <!-- Auth resolved — delegate to router + guard -->
    <template v-else>
      <a
        v-if="showAppHeader"
        class="skip-link"
        href="#main-content"
        @click="focusMainContent"
      >ข้ามไปยังเนื้อหาหลัก</a>
      <AppHeader v-if="showAppHeader" />
      <div id="main-content" class="app-main" tabindex="-1" aria-label="เนื้อหาหลัก">
        <RouterView />
      </div>
    </template>
  </div>
</template>

<style scoped>
.app {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.app-main {
  flex: 1;
  min-width: 0;
}

/* Keep the skip target programmatically focusable without adding a visible
   focus ring after ordinary mouse navigation. */
.app-main:focus {
  outline: none;
}

.skip-link {
  position: fixed;
  top: 0.5rem;
  left: 0.75rem;
  z-index: 1000;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--brand);
  border-radius: var(--radius-chip);
  background: var(--surface);
  color: var(--text);
  font-weight: 700;
  text-decoration: none;
  transform: translateY(-200%);
  transition: transform 0.12s ease;
}

.skip-link:focus {
  transform: translateY(0);
  outline: 2px solid var(--brand-light);
  outline-offset: 2px;
}

/* ── Branded splash ─────────────────────────────────────────────────────── */
.app-splash {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  flex: 1;
}

.app-splash__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

.app-splash__wordmark {
  font-family: 'Kanit', 'Sarabun', sans-serif;
  font-weight: 600;
  font-size: 1.6rem;
  letter-spacing: 0.08em;
  color: var(--text-dim);
}

.app-splash__wordmark-accent {
  color: var(--brand-light);
  font-weight: 700;
}

.app-splash__spinner {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid var(--line-light);
  border-top-color: var(--brand-light);
  border-radius: var(--radius-round);
  animation: splash-spin 0.7s linear infinite;
}

@keyframes splash-spin {
  to { transform: rotate(360deg); }
}
</style>
